import { useEffect, useRef, useState, FormEvent } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const interests = ['ManageEngine', 'AutomMate', 'Zoho ONE', 'Entegrasyon', 'Diğer'];

const infoItems = [
  { icon: 'ri-time-line', title: 'Hızlı Yanıt', desc: '24 saat içinde uzmanlarımız sizi arar.' },
  { icon: 'ri-shield-check-line', title: 'Güvenli İletişim', desc: 'Bilgileriniz KVKK kapsamında korunur.' },
  { icon: 'ri-customer-service-2-line', title: 'Uzman Danışmanlık', desc: 'Sektör deneyimli IT uzmanlarıyla görüşün.' },
];

const ContactForm = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', company: '', email: '', phone: '', interest: '', message: '' });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.cf-left', { scrollTrigger: { trigger: '.cf-left', start: 'top 80%' }, opacity: 0, x: -30, duration: 0.9, ease: 'power3.out' });
      gsap.from('.cf-right', { scrollTrigger: { trigger: '.cf-right', start: 'top 80%' }, opacity: 0, x: 30, duration: 0.9, ease: 'power3.out' });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.interest) return;
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d4f0fqqsco6c4fkvqkdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setIsSubmitted(true);
      setFormData({ name: '', company: '', email: '', phone: '', interest: '', message: '' });
    } catch (err) {
      console.error('Form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 bg-white border border-[#E2E8F0] rounded-lg text-[#0F172A] text-sm focus:border-[#0891B2] focus:outline-none focus:ring-2 focus:ring-[#0891B2]/15 transition-all duration-200 font-body placeholder:text-[#94A3B8]";

  return (
    <section ref={sectionRef} id="contact" className="py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#ECFEFF] border border-[#0891B2]/30 text-[#0E7490] text-xs font-semibold mb-5 font-body">
            <i className="ri-mail-send-line"></i>
            İletişim
          </div>
          <h2 className="text-4xl font-extrabold text-[#0F172A] mb-4 font-heading leading-tight">
            Projenizi Birlikte<br />
            <span className="text-[#0891B2]">Değerlendirelim</span>
          </h2>
          <p className="text-base text-[#475569] font-body">
            WOTECH uzmanları <strong className="text-[#0F172A]">24 saat içinde</strong> sizinle iletişime geçer.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left info */}
          <div className="cf-left lg:col-span-2">
            <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] mb-6">
              <img
                src="https://public.readdy.ai/ai/img_res/828d3c17d63aaea168dcd971b4b8e47a.jpg"
                alt="WOTECH Danışmanlık"
                title="WOTECH IT Danışmanlık"
                className="w-full h-44 object-cover object-top"
              />
            </div>
            <div className="space-y-4">
              {infoItems.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-[#E2E8F0]">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#ECFEFF] flex-shrink-0">
                    <i className={`${item.icon} text-[#0891B2] text-base`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#0F172A] font-heading">{item.title}</div>
                    <div className="text-xs text-[#64748B] font-body mt-0.5">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right form */}
          <div className="cf-right lg:col-span-3">
            {isSubmitted ? (
              <div className="p-12 rounded-2xl bg-white border border-[#E2E8F0] text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 rounded-full bg-[#ECFEFF] border border-[#0891B2]/20">
                  <i className="ri-check-line text-3xl text-[#0891B2]"></i>
                </div>
                <h3 className="text-xl font-extrabold text-[#0F172A] mb-2 font-heading">Mesajınız Alındı!</h3>
                <p className="text-sm text-[#475569] font-body mb-6">En kısa sürede sizinle iletişime geçeceğiz.</p>
                <button onClick={() => setIsSubmitted(false)} className="px-6 py-3 bg-[#0891B2] text-white font-semibold rounded-lg hover:bg-[#0E7490] transition-all duration-200 cursor-pointer font-body">
                  Yeni Mesaj Gönder
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form id="wotech-contact-form" className="p-8 rounded-2xl bg-white border border-[#E2E8F0]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#334155] mb-1.5 font-body">Ad Soyad *</label>
                    <input type="text" name="name" required placeholder="Adınız Soyadınız" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#334155] mb-1.5 font-body">Firma *</label>
                    <input type="text" name="company" required placeholder="Firma Adı" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#334155] mb-1.5 font-body">E-posta *</label>
                    <input type="email" name="email" required placeholder="ornek@firma.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#334155] mb-1.5 font-body">Telefon *</label>
                    <input type="tel" name="phone" required placeholder="+90 5XX XXX XX XX" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputCls} />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-[#334155] mb-2 font-body">İlgi Alanı *</label>
                  <div className="flex flex-wrap gap-2">
                    {interests.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setFormData({ ...formData, interest: item })}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer font-body border ${
                          formData.interest === item
                            ? 'bg-[#0891B2] text-white border-[#0891B2]'
                            : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0891B2]/40 hover:text-[#0891B2]'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="interest" value={formData.interest} />
                </div>

                <div className="mb-6">
                  <label className="block text-xs font-semibold text-[#334155] mb-1.5 font-body">Mesaj</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Projeniz hakkında kısaca bilgi verin..."
                    value={formData.message}
                    onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }}
                    className={`${inputCls} resize-none`}
                  />
                  <p className="text-xs text-[#94A3B8] mt-1 text-right font-body">{formData.message.length}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.interest}
                  className="w-full py-3.5 bg-[#0891B2] text-white font-bold rounded-lg hover:bg-[#0E7490] transition-all duration-200 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer font-body flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <><i className="ri-loader-4-line animate-spin text-lg"></i> Gönderiliyor...</>
                  ) : (
                    <><i className="ri-send-plane-fill text-lg"></i> Hemen İletişime Geç</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
